import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { flexRender, type Table } from '@tanstack/react-table';

import { Checkbox } from '@/components/ui/checkbox';

import type { DataTableRow, TableAction } from '../types';
import { RowActions } from './row-actions';

interface DataTableCardsProps<T extends DataTableRow> {
  table: Table<T>;
  selectable?: boolean;
  isRowSelectable?: (row: T) => boolean;
  actions?: TableAction<T>[];
}

/**
 * The rows of a flat table as cards, for a phone: the first visible column is the
 * card title next to the checkbox and the actions, the others are label / value
 * pairs under it. Same columns, cells and selection as the table.
 */
export function DataTableCards<T extends DataTableRow>({
  table,
  selectable = false,
  isRowSelectable,
  actions = []
}: DataTableCardsProps<T>) {
  const { t } = useTranslation();
  const headers = new Map(
    table
      .getFlatHeaders()
      .map((header) => [header.column.id, flexRender(header.column.columnDef.header, header.getContext()) as ReactNode])
  );

  return (
    <ul className="divide-y">
      {table.getRowModel().rows.map((row) => {
        const [titleCell, ...cells] = row.getVisibleCells();
        const render = (cell: typeof titleCell) =>
          cell.column.columnDef.cell
            ? flexRender(cell.column.columnDef.cell, cell.getContext())
            : (cell.getValue() as ReactNode);

        return (
          <li key={row.id} className="space-y-3 px-4 py-3">
            <div className="flex items-start gap-3">
              {selectable && (isRowSelectable?.(row.original) ?? true) && (
                <Checkbox
                  className="mt-0.5"
                  checked={row.getIsSelected()}
                  onCheckedChange={(value) => row.toggleSelected(!!value)}
                  aria-label={t('common.table.selectRow', { id: row.id, defaultValue: `Select row ${row.id}` })}
                />
              )}
              <div className="min-w-0 flex-1 text-sm font-medium wrap-break-word">{titleCell && render(titleCell)}</div>
              {actions.length > 0 && <RowActions actions={actions} row={row.original} />}
            </div>
            {cells.length > 0 && (
              <dl className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-4 gap-y-2 text-sm">
                {cells.map((cell) => (
                  <div key={cell.id} className="contents">
                    <dt className="text-muted-foreground">{headers.get(cell.column.id)}</dt>
                    <dd className="wrap-break-word">{render(cell)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </li>
        );
      })}
    </ul>
  );
}
